<template>
  <div class="access-gate">
    <div class="access-panel">
      <p class="access-kicker">PLENTY // INTERNAL ACCESS</p>
      <h1>{{ $t('accessGate.title') }}</h1>
      <p class="access-desc">{{ hintText }}</p>

      <form class="code-form" @submit.prevent="submit">
        <label class="code-label" for="access-code">{{ $t('accessGate.codeLabel') }}</label>
        <input
          id="access-code"
          ref="codeInput"
          v-model="code"
          class="code-input"
          type="password"
          inputmode="numeric"
          pattern="[0-9]*"
          maxlength="4"
          autocomplete="one-time-code"
          :placeholder="$t('accessGate.codePlaceholder')"
          :disabled="loading"
          @input="onInput"
        />
        <p v-if="error" class="access-error">{{ error }}</p>
        <button class="submit-btn" type="submit" :disabled="loading || code.length !== 4">
          {{ loading ? $t('accessGate.verifying') : $t('accessGate.submit') }}
        </button>
      </form>
    </div>
  </div>
</template>

<script>
import { verifyPlatformAccessCode } from '@/utils/accessGate'

export default {
  name: 'AccessGate',
  props: {
    hintZh: {
      type: String,
      default: ''
    },
    hintEn: {
      type: String,
      default: ''
    }
  },
  emits: ['success'],
  data() {
    return {
      code: '',
      loading: false,
      error: ''
    }
  },
  computed: {
    hintText() {
      const locale = this.$i18n?.locale || 'en'
      if (String(locale).toLowerCase().startsWith('zh')) {
        return this.hintZh || this.$t('accessGate.defaultHint')
      }
      return this.hintEn || this.hintZh || this.$t('accessGate.defaultHint')
    }
  },
  mounted() {
    this.$refs.codeInput?.focus()
  },
  methods: {
    onInput() {
      this.code = String(this.code || '').replace(/\D/g, '').slice(0, 4)
      this.error = ''
    },
    async submit() {
      if (this.code.length !== 4 || this.loading) return
      this.loading = true
      this.error = ''
      try {
        await verifyPlatformAccessCode(this.code)
        this.$emit('success')
      } catch (e) {
        if (e.code === 'ACCESS_RATE_LIMITED') {
          this.error = e.message
        } else {
          this.error = this.$t('accessGate.invalidCode')
        }
        this.code = ''
        this.$refs.codeInput?.focus()
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.access-gate {
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  background:
    radial-gradient(circle at 20% 20%, rgba(56, 189, 248, 0.12), transparent 40%),
    radial-gradient(circle at 80% 0%, rgba(16, 185, 129, 0.1), transparent 35%),
    #071018;
  color: #e2e8f0;
}

.access-panel {
  width: min(100%, 420px);
  padding: 28px 22px;
  border: 1px solid rgba(148, 163, 184, 0.25);
  border-radius: 18px;
  background: rgba(15, 23, 42, 0.82);
  box-shadow: 0 24px 60px rgba(2, 6, 23, 0.45);
}

.access-kicker {
  margin: 0 0 10px;
  font-size: 11px;
  letter-spacing: 0.14em;
  color: #67e8f9;
}

.access-panel h1 {
  margin: 0 0 10px;
  font-size: 24px;
  line-height: 1.2;
}

.access-desc {
  margin: 0 0 22px;
  color: #94a3b8;
  font-size: 14px;
  line-height: 1.6;
}

.code-form {
  display: grid;
  gap: 12px;
}

.code-label {
  font-size: 13px;
  color: #cbd5e1;
}

.code-input {
  width: 100%;
  padding: 14px 16px;
  border-radius: 12px;
  border: 1px solid rgba(148, 163, 184, 0.35);
  background: rgba(2, 6, 23, 0.65);
  color: #f8fafc;
  font-size: 28px;
  letter-spacing: 0.45em;
  text-align: center;
  outline: none;
}

.code-input:focus {
  border-color: #38bdf8;
  box-shadow: 0 0 0 3px rgba(56, 189, 248, 0.18);
}

.access-error {
  margin: 0;
  color: #fca5a5;
  font-size: 13px;
}

.submit-btn {
  margin-top: 4px;
  width: 100%;
  border: 0;
  border-radius: 12px;
  padding: 14px 16px;
  background: linear-gradient(135deg, #0284c7, #0891b2);
  color: #fff;
  font-size: 15px;
  font-weight: 600;
}

.submit-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
</style>
